import type { Hero } from 'types/hero';
import type { Media } from 'types/media';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';

import { Button, Wrap } from '@leafwell/components';

import { request } from 'lib/rest';

import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';
import { toQueryString } from 'lib/utils/toQueryString';

const AffiliateLogo = dynamic(() => import('./affiliate-logo'), {
  loading: () => <div className="h-16"></div>,
  ssr: false,
});

type PartnerProps = {
  logo?: Media;
  website?: string;
  partnerMessage?: string;
};

const HeroSecondary: React.FC<Hero> = ({
  title,
  description,
  image,
  button,
  downloadButton,
}) => {
  const { asPath, query } = useRouter();
  const [partnerData, setPartnerData] = useState<PartnerProps | null>(null);
  const { trackEvent } = useEventsTracker();
  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const queryParams = toQueryString(query);
  const heroName = asPath?.split('?');
  useEffect(() => {
    if (product) {
      setNewUrl(
        button?.url?.includes('?')
          ? `${button.url}&session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`
          : `${button.url}?session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`,
      );
    }
  }, [sessionId, distinctId, product, queryParams]);

  useEffect(() => {
    if (query.utm_source && !partnerData && asPath?.includes('/membership')) {
      // call api to fetch partner data based on `utm_source`
      request(`/partner?slug=${query.utm_source}`).then(res => {
        if (res.data && res.data?.totalItems > 0) {
          const { acf } = camelcaseKeys(res.data.items[0], {
            deep: true,
          });
          const { logo, website, membership } = acf;

          setPartnerData({
            logo,
            website,
            partnerMessage: membership?.partnerMessage,
          });
        }
      });
    }
  }, [query]);

  return (
    <section
      className="bg-lilac-50 xl:-mt-10 xl:pt-10"
      itemID={`Hero${heroName[0]?.replace('/', '-')}`}
    >
      <Wrap
        className="gap-y-8 lg:grid-cols-2 lg:gap-x-16 items-start"
        spacer={true}
      >
        {title || description ? (
          <div className="grid gap-y-10">
            {title && (
              <HeadingBody
                level="1"
                tagName="h1"
                title={title}
                className="font-semibold"
              />
            )}
            {description && (
              <WysiwygContent
                content={description}
                className="!text-lg xl:!text-xl !text-gray-700"
              />
            )}
            {partnerData?.logo || partnerData?.partnerMessage ? (
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                {partnerData?.logo ? (
                  <AffiliateLogo
                    affiliateLogo={partnerData.logo}
                    affiliateWebsite={partnerData.website}
                  />
                ) : null}
                {partnerData?.partnerMessage ? (
                  <WysiwygContent
                    content={partnerData?.partnerMessage}
                    className="!text-lg xl:!text-xl !text-gray-700 lg:max-w-sm"
                  />
                ) : null}
              </div>
            ) : null}
            {button || downloadButton ? (
              <div className="flex flex-col lg:flex-row gap-4">
                {downloadButton && (
                  <Button
                    href={downloadButton.url}
                    label={downloadButton.title}
                    rounded={true}
                    filled={true}
                    variant="secondary"
                    target={downloadButton.target}
                    className="w-fit h-fit"
                    icon="download"
                    onClick={() => {
                      trackEvent({
                        event: 'select_content',
                        content_type: downloadButton.title,
                        item_id: `Hero${heroName[0]?.replace('/', '-')}`,
                        action: 'Hero-download-file',
                      });
                    }}
                  />
                )}
                {button && (
                  <Button
                    href={product ? newUrl : button.url}
                    label={button.title}
                    rounded={true}
                    filled={downloadButton ? false : true}
                    variant="secondary"
                    target={button.target}
                    className="w-fit h-fit"
                    icon="arrow"
                    onClick={() => {
                      trackEvent({
                        event: 'select_content',
                        content_type: button.title,
                        item_id: `Hero${heroName[0]?.replace('/', '-')}`,
                        action: 'Hero-click-cta',
                      });

                      if (product) {
                        trackEvent({
                          event: 'generate_lead',
                          item_id: `Hero${heroName[0]?.replace('/', '-')}`,
                          service: product,
                          page_referrer: document.referrer,
                        });
                      }
                    }}
                  />
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        {image && (
          <Figure
            {...image}
            className="order-first lg:order-last lg:justify-self-end"
          />
        )}
      </Wrap>
    </section>
  );
};

export default HeroSecondary;
